import {
  LuSparkles,
  LuCamera,
  LuAperture,
  LuLightbulb,
  LuPalette,
  LuUser,
  LuUserRound,
  LuMapPin,
  LuBox,
} from "react-icons/lu";
import { ArtifactScope } from "@kagami/shared/artifactScope";
import { ArtifactVisibility } from "@kagami/shared/artifactVisibility";

export const SCOPE_ICONS = {
  [ArtifactScope.Camera]: <LuCamera />,
  [ArtifactScope.Lens]: <LuAperture />,
  [ArtifactScope.Light]: <LuLightbulb />,
  [ArtifactScope.Style]: <LuPalette />,
  [ArtifactScope.Character]: <LuUserRound />,
  [ArtifactScope.Location]: <LuMapPin />,
  [ArtifactScope.Prop]: <LuBox />,
  [ArtifactScope.User]: <LuUser />,
};

const isSelectableBy = (artifact, userId) =>
  artifact.visibility === ArtifactVisibility.Public ||
  !artifact.user ||
  String(artifact.user.id) === String(userId);

const toMentionItem = (artifact) => ({
  id: artifact._id,
  label: artifact.name || "Untitled artifact",
  description: artifact.description || undefined,
  icon: SCOPE_ICONS[artifact.scope] || <LuSparkles />,
  data: { scope: artifact.scope },
});

/**
 * @input ([{_id:'1',name:'Max',scope:'character',visibility:'public'}], 'u1')
 * @output [{id:'1',label:'Max',description:undefined,icon:<LuUserRound/>,data:{scope:'character'}}]
 */
export const characterArtifactItems = (artifacts, userId) =>
  artifacts
    .filter(
      (artifact) =>
        artifact.scope === ArtifactScope.Character &&
        isSelectableBy(artifact, userId),
    )
    .map(toMentionItem);

/**
 * @input ([{_id:'3',name:'Rooftop',scope:'location',visibility:'public'}], 'u1')
 * @output [{id:'3',label:'Rooftop',description:undefined,icon:<LuMapPin/>,data:{scope:'location'}}]
 */
export const locationArtifactItems = (artifacts, userId) =>
  artifacts
    .filter(
      (artifact) =>
        artifact.scope === ArtifactScope.Location &&
        isSelectableBy(artifact, userId),
    )
    .map(toMentionItem);

/**
 * @input ([{_id:'4',name:'Sword',scope:'prop',visibility:'public'}], 'u1')
 * @output [{id:'4',label:'Sword',description:undefined,icon:<LuBox/>,data:{scope:'prop'}}]
 */
export const propArtifactItems = (artifacts, userId) =>
  artifacts
    .filter(
      (artifact) =>
        artifact.scope === ArtifactScope.Prop &&
        isSelectableBy(artifact, userId),
    )
    .map(toMentionItem);

/**
 * Everything else — camera/lens/light/style/instruction/user — excludes the
 * scopes that already have their own dedicated trigger (`#` character, `@`
 * location, `&` prop) so they aren't offered twice under `/`.
 *
 * @input ([{_id:'2',name:'Wide',scope:'lens',visibility:'private',user:'u1'}], 'u1')
 * @output [{id:'2',label:'Wide',description:undefined,icon:<LuAperture/>,data:{scope:'lens'}}]
 */
const DEDICATED_TRIGGER_SCOPES = new Set([
  ArtifactScope.Character,
  ArtifactScope.Location,
  ArtifactScope.Prop,
]);

export const nonCharacterArtifactItems = (artifacts, userId) =>
  artifacts
    .filter(
      (artifact) =>
        !DEDICATED_TRIGGER_SCOPES.has(artifact.scope) &&
        isSelectableBy(artifact, userId),
    )
    .map(toMentionItem);
